import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface JwtPayload {
  sub: string;
  email: string;
  roles: string[];
  organizationId: string;
}

interface AuthUser {
  id: string;
  email: string;
  roles: string[];
  organizationId: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  login: (email: string, password: string, slug: string) => Promise<void>;
  register: (firstName: string, lastName: string, email: string, password: string, slug: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function parseJwt(token: string): JwtPayload | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as JwtPayload;
  } catch {
    return null;
  }
}

const TOKEN_KEY = 'shufflr_token';

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<AuthUser | null>(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return null;
    const p = parseJwt(t);
    if (!p) return null;
    return { id: p.sub, email: p.email, roles: p.roles, organizationId: p.organizationId };
  });

  const applyToken = useCallback((t: string) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    const p = parseJwt(t);
    if (p) setUser({ id: p.sub, email: p.email, roles: p.roles, organizationId: p.organizationId });
  }, []);

  const login = useCallback(async (email: string, password: string, slug: string) => {
    const res = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-org-subdomain': slug },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message ?? 'Login failed');
    }
    const { accessToken } = await res.json() as { accessToken: string };
    applyToken(accessToken);
  }, [applyToken]);

  const register = useCallback(async (firstName: string, lastName: string, email: string, password: string, slug: string) => {
    const res = await fetch('/api/v1/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-org-subdomain': slug },
      body: JSON.stringify({ firstName, lastName, email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = (err as { message?: string | string[] }).message;
      throw new Error(Array.isArray(msg) ? msg[0] : (msg ?? 'Registration failed'));
    }
    const { accessToken } = await res.json() as { accessToken: string };
    applyToken(accessToken);
  }, [applyToken]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
